/* eslint-disable @typescript-eslint/prefer-nullish-coalescing */
import dayjs from 'dayjs';
import React from 'react';
import { Box, Label } from '@apia/theme';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import {
  TApiaFilter,
  TApiaFilterValue,
  formatMessage,
  getDateFormat,
} from '@apia/util';
import { ApiaFilter, RequiredMark } from '@apia/components';
import { TResponsiveTableContextProps } from '../types';
import { useResponsiveTableContext } from '../ResponsiveTableContext';
import { RangeFilter } from './RangeFilter';
import { responsiveTableStore } from '../store';

dayjs.extend(customParseFormat);

const dateFormat = getDateFormat();

function controlDatesOrder(first: string, last: string) {
  if (!first || !last) return true;

  if (dayjs(last, dateFormat).isBefore(dayjs(first, dateFormat))) {
    return window.MSG_FEC_FIN_MAY_FEC_INI;
  }
  return true;
}

const NoMemoInnerRender = React.forwardRef(
  (
    {
      filters,
      tableName,
    }: {
      filters: TResponsiveTableContextProps['filters'];
      isResponsive?: boolean;
      tableName?: string;
    },
    ref,
  ) => {
    const {
      onFilterBlur,
      onFilterChange,
      onFilterPressEnter,
      name,
      FiltersRenderer,
    } = useResponsiveTableContext(tableName);
    const [lastEmittedValue, setLastEmittedValue] =
      React.useState<TApiaFilterValue | null>(null);
    const [lastEmittedValueByPartner, setLastEmittedValueByPartner] =
      React.useState<TApiaFilterValue | null>(null);

    return (
      <Box ref={ref} className="filtersRender">
        {filters?.map((filter, key) => {
          if (filter.hide) return null;

          const Renderer = FiltersRenderer?.(String(filter.id));

          const isDate = filter.type === 'D' || filter.type === 'date';
          const filterToId = filter.filterToId ?? `${filter.id}i`;
          const filterTo: TApiaFilter = {
            ...filter,
            hide: filter.filterToId === undefined,
            id: filterToId,
            currentValue: filter.filterToValue ?? '',
            required: false,
          };

          const column = responsiveTableStore
            .getState()
            .responsiveTableSlice[
              name
            ].columns.find((current) => current.name === filter.column);
          const filterTitle =
            filter.title ||
            column?.label ||
            column?.title ||
            column?.name ||
            'noFilterNorColumnTitle';

          return (
            <React.Fragment key={filter.id ?? filter.column ?? key}>
              <Box
                className={
                  filter.type === 'D' || filter.type === 'date'
                    ? 'twoColumns'
                    : ''
                }
                key={filter.id ?? filter.column ?? key}
              >
                <Label
                  title={
                    isDate ? window.LBL_DATE_FILTER_FROM_TOOLTIP : filterTitle
                  }
                >
                  {window.SHOW_REQUIRED_POSITION !== '0' && (
                    <RequiredMark isRequired={filter.required} />
                  )}
                  {!filter?.avoidLabel && (
                    <Box as="span" className="filterRenderLabel">
                      {isDate && !filter.hideToFilter
                        ? formatMessage(window.LBL_DATE_FILTER_FROM_TXT, {
                            TOK1: filterTitle,
                          })
                        : filterTitle}
                    </Box>
                  )}
                  {Renderer ? (
                    <Renderer filter={filter} />
                  ) : filter.isRange ? (
                    <RangeFilter filter={filter} />
                  ) : (
                    <ApiaFilter
                      filter={filter}
                      onChange={(currentValue) => {
                        const result = controlDatesOrder(
                          currentValue as string,
                          lastEmittedValueByPartner as string,
                        );
                        if (result === true) {
                          setLastEmittedValue(currentValue);
                          if (onFilterChange)
                            void onFilterChange({ ...filter, currentValue });
                        }
                        return result;
                      }}
                      onBlur={(currentValue) => {
                        if (onFilterBlur)
                          void onFilterBlur({ ...filter, currentValue });
                      }}
                      onPressEnter={(currentValue) => {
                        if (onFilterPressEnter)
                          void onFilterPressEnter({
                            ...filter,
                            currentValue,
                          });
                      }}
                    />
                  )}
                  {window.SHOW_REQUIRED_POSITION === '0' && (
                    <RequiredMark isRequired={filter.required} />
                  )}
                </Label>
              </Box>
              {isDate && !filter.hideToFilter && (
                <Box
                  className={
                    filter.type === 'D' || filter.type === 'date'
                      ? 'twoColumns'
                      : ''
                  }
                >
                  <Label title={window.LBL_DATE_FILTER_TO_TOOLTIP}>
                    {!filter?.avoidLabel && (
                      <Box as="span" className="filterRenderLabel">
                        {formatMessage(window.LBL_DATE_FILTER_TO_TXT, {
                          TOK1: filterTitle,
                        })}
                      </Box>
                    )}
                    <ApiaFilter
                      filter={filterTo}
                      onChange={(currentValue) => {
                        const result = controlDatesOrder(
                          lastEmittedValue as string,
                          currentValue as string,
                        );
                        if (result === true) {
                          setLastEmittedValueByPartner(currentValue);
                          if (onFilterChange)
                            void onFilterChange({
                              ...filter,
                              filterToId,
                              filterToValue: currentValue,
                            });
                        }
                        return result;
                      }}
                      onBlur={(currentValue) => {
                        if (onFilterBlur)
                          void onFilterBlur({
                            ...filter,
                            filterToId,
                            filterToValue: currentValue,
                          });
                      }}
                      onPressEnter={(currentValue) => {
                        if (onFilterPressEnter)
                          void onFilterPressEnter({
                            ...filter,
                            filterToId,
                            filterToValue: currentValue,
                          });
                      }}
                    />
                  </Label>
                </Box>
              )}
            </React.Fragment>
          );
        })}
      </Box>
    );
  },
);

NoMemoInnerRender.displayName = 'FiltersInnerRender';

export const InnerRender = React.memo(NoMemoInnerRender);
